<template>
	<div
		class="modal fade"
		:id="modalId"
		tabindex="-1"
		:aria-labelledby="modalId + 'Label'"
		aria-hidden="true"
	>
		<div class="modal-dialog">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title" :id="modalId + 'Label'">Update Service Data</h5>
					<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
				</div>
				<div class="modal-body">
					<div v-if="service" class="alert alert-light border mb-3 py-2 small">
						<div><strong>IMEI:</strong> {{ service.imei || "—" }}</div>
						<div>
							<strong>Current total:</strong> ₹{{ formatMoney(service.total_amount) }}
							<span class="ms-2"><strong>Paid:</strong> ₹{{ formatMoney(currentPaid) }}</span>
						</div>
					</div>

					<form @submit.prevent="submit">
						<div class="mb-3">
							<label class="control-label">Total amount <span class="text-danger">*</span></label>
							<input
								type="number"
								step="0.01"
								min="0"
								class="form-control"
								v-model="form.amount"
								:class="{ 'is-invalid': errors.amount }"
							/>
							<div v-if="errors.amount" class="invalid-feedback d-block">{{ errors.amount }}</div>
						</div>

						<div class="mb-3">
							<label class="control-label">Payment user type <span class="text-danger">*</span></label>
							<select
								class="form-select"
								v-model="form.payment_by"
								@change="onPaymentByChange"
								:class="{ 'is-invalid': errors.payment_by }"
							>
								<option value="1">Dealer</option>
								<option value="2">Customer</option>
							</select>
							<div v-if="errors.payment_by" class="invalid-feedback d-block">{{ errors.payment_by }}</div>
						</div>

						<div class="mb-3 position-relative" v-if="form.payment_by === '1'">
							<label class="control-label">Dealer <span class="text-danger">*</span></label>
							<input
								type="text"
								class="form-control"
								placeholder="Search dealer"
								v-model="dealerSearch"
								@focus="showDealerSuggestions = true"
								@input="showDealerSuggestions = true"
								@blur="hideDealerSuggestions"
								:class="{ 'is-invalid': errors.payment_by_id }"
								autocomplete="off"
							/>
							<div
								v-if="showDealerSuggestions"
								class="list-group position-absolute w-100 shadow-sm"
								style="z-index: 1055; max-height: 220px; overflow-y: auto"
							>
								<a
									href="#"
									class="list-group-item list-group-item-action"
									v-for="d in filteredDealers"
									:key="'upd-dealer-' + d.id"
									@mousedown.prevent="selectDealer(d)"
								>
									{{ d.first_name }}
									<span v-if="d.mobile" class="text-muted small"> — {{ d.mobile }}</span>
								</a>
								<div v-if="!filteredDealers.length" class="list-group-item text-muted">No matches</div>
							</div>
							<div v-if="selectedDealer" class="form-text">
								Selected: {{ selectedDealer.first_name }}
								<span v-if="form.payment_by_id">(user #{{ form.payment_by_id }})</span>
							</div>
							<div v-if="errors.payment_by_id" class="invalid-feedback d-block">{{ errors.payment_by_id }}</div>
						</div>

						<div class="mb-3 position-relative" v-if="form.payment_by === '2'">
							<label class="control-label">Customer <span class="text-danger">*</span></label>
							<input
								type="text"
								class="form-control"
								placeholder="Search customer"
								v-model="customerSearch"
								@focus="showCustomerSuggestions = true"
								@input="showCustomerSuggestions = true"
								@blur="hideCustomerSuggestions"
								:class="{ 'is-invalid': errors.payment_by_id }"
								autocomplete="off"
							/>
							<div
								v-if="showCustomerSuggestions"
								class="list-group position-absolute w-100 shadow-sm"
								style="z-index: 1055; max-height: 220px; overflow-y: auto"
							>
								<a
									href="#"
									class="list-group-item list-group-item-action"
									v-for="c in filteredCustomers"
									:key="'upd-cust-' + c.id"
									@mousedown.prevent="selectCustomer(c)"
								>
									{{ c.company_name || c.name || ("Customer #" + c.id) }}
									<span v-if="c.mobile" class="text-muted small"> — {{ c.mobile }}</span>
								</a>
								<div v-if="!filteredCustomers.length" class="list-group-item text-muted">No matches</div>
							</div>
							<div v-if="selectedCustomer" class="form-text">
								Selected: {{ selectedCustomer.company_name || selectedCustomer.name }}
								<span v-if="form.payment_by_id">(user #{{ form.payment_by_id }})</span>
							</div>
							<div v-if="errors.payment_by_id" class="invalid-feedback d-block">{{ errors.payment_by_id }}</div>
						</div>

						<template v-if="hasExistingPayment">
							<hr />
							<p class="text-muted small mb-2">Payment already recorded — you can correct paid amounts and payment ID.</p>
							<div class="row">
								<div class="col-md-4 mb-3">
									<label class="control-label">Cash</label>
									<input type="number" step="0.01" min="0" class="form-control" v-model="form.cash_in_hand" :class="{ 'is-invalid': errors.cash_in_hand }" />
									<div v-if="errors.cash_in_hand" class="invalid-feedback d-block">{{ errors.cash_in_hand }}</div>
								</div>
								<div class="col-md-4 mb-3">
									<label class="control-label">Online</label>
									<input type="number" step="0.01" min="0" class="form-control" v-model="form.cash_online" :class="{ 'is-invalid': errors.cash_online }" />
									<div v-if="errors.cash_online" class="invalid-feedback d-block">{{ errors.cash_online }}</div>
								</div>
								<div class="col-md-4 mb-3">
									<label class="control-label">Credit</label>
									<input type="number" step="0.01" min="0" class="form-control" v-model="form.credit_amount" :class="{ 'is-invalid': errors.credit_amount }" />
									<div v-if="errors.credit_amount" class="invalid-feedback d-block">{{ errors.credit_amount }}</div>
								</div>
							</div>
							<div class="mb-3" v-if="showPaymentIdField">
								<label class="control-label">Payment ID <span v-if="needsPaymentId" class="text-danger">*</span></label>
								<input type="text" class="form-control" v-model="form.payment_id" :class="{ 'is-invalid': errors.payment_id }" />
								<div v-if="errors.payment_id" class="invalid-feedback d-block">{{ errors.payment_id }}</div>
							</div>
						</template>

						<div v-if="errors.form" class="alert alert-danger py-2">{{ errors.form }}</div>
					</form>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-light" @click="close" :disabled="submitting">Close</button>
					<button type="button" class="btn btn-primary" @click="submit" :disabled="submitting">
						<span v-if="submitting">Saving…</span>
						<span v-else>Update</span>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Modal } from "bootstrap";

export default {
	name: "UpdateServiceDataModal",
	emits: ["updated"],
	props: {
		modalId: {
			type: String,
			default: "updateServiceDataModal",
		},
	},
	data() {
		return {
			token: "",
			modalInstance: null,
			service: null,
			dealers: [],
			companies: [],
			dealerSearch: "",
			customerSearch: "",
			selectedDealer: null,
			selectedCustomer: null,
			showDealerSuggestions: false,
			showCustomerSuggestions: false,
			submitting: false,
			errors: {},
			form: {
				amount: "",
				payment_by: "1",
				payment_by_id: "",
				cash_in_hand: "",
				cash_online: "",
				credit_amount: "",
				payment_id: "",
			},
		};
	},
	computed: {
		currentPaid() {
			if (!this.service) return 0;
			return (
				(parseFloat(this.service.cash_in_hand) || 0) +
				(parseFloat(this.service.cash_online) || 0) +
				(parseFloat(this.service.credit_amount) || 0)
			);
		},
		hasExistingPayment() {
			if (!this.service) return false;
			const paid = this.currentPaid;
			const status = Number(this.service.payment_status);
			return paid > 0.01 || status === 1 || status === 3 || status === 5;
		},
		showPaymentIdField() {
			return this.hasExistingPayment;
		},
		needsPaymentId() {
			return (parseFloat(this.form.cash_online) || 0) > 0.01;
		},
		filteredDealers() {
			const q = (this.dealerSearch || "").trim().toLowerCase();
			if (!q) return this.dealers.slice(0, 50);
			return this.dealers
				.filter((d) => (d.first_name || "").toLowerCase().includes(q) || String(d.mobile || "").includes(q))
				.slice(0, 50);
		},
		filteredCustomers() {
			const q = (this.customerSearch || "").trim().toLowerCase();
			const list = this.companies || [];
			if (!q) return list.slice(0, 50);
			return list
				.filter((c) => {
					const name = (c.company_name || c.name || "").toLowerCase();
					return name.includes(q) || String(c.mobile || "").includes(q);
				})
				.slice(0, 50);
		},
	},
	mounted() {
		const el = document.getElementById(this.modalId);
		if (el) this.modalInstance = new Modal(el);
		this.fetchDealers();
		this.fetchCompanies();
	},
	beforeUnmount() {
		if (this.modalInstance) {
			this.modalInstance.dispose();
			this.modalInstance = null;
		}
	},
	methods: {
		getToken() {
			this.token = "";
			try {
				const staff = JSON.parse(localStorage.getItem("airotrack_staff") || "null");
				if (staff?.token) this.token = staff.token;
			} catch (e) {
				this.token = localStorage.getItem("token") || "";
			}
			if (!this.token) this.token = localStorage.getItem("token") || "";
		},

		formatMoney(v) {
			const n = parseFloat(v);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		getSelectedUserId(entity) {
			return entity && entity.user_id != null && entity.user_id !== "" ? String(entity.user_id) : "";
		},

		fetchDealers() {
			this.getToken();
			return this.$axios
				.get("/staffs/dealers", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.dealers = Array.isArray(data) ? data : data.data || [];
				})
				.catch(() => {
					this.dealers = [];
				});
		},

		fetchCompanies() {
			this.getToken();
			return this.$axios
				.get("/staffs/companies", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.companies = Array.isArray(data) ? data : data.data || [];
				})
				.catch(() => {
					this.companies = [];
				});
		},

		onPaymentByChange() {
			this.form.payment_by_id = "";
			this.selectedDealer = null;
			this.selectedCustomer = null;
			this.dealerSearch = "";
			this.customerSearch = "";
			this.errors.payment_by_id = "";
			this.errors.payment_by = "";
		},

		hideDealerSuggestions() {
			setTimeout(() => {
				this.showDealerSuggestions = false;
			}, 200);
		},

		hideCustomerSuggestions() {
			setTimeout(() => {
				this.showCustomerSuggestions = false;
			}, 200);
		},

		selectDealer(d) {
			this.selectedDealer = d;
			this.dealerSearch = d.first_name || "";
			this.form.payment_by_id = this.getSelectedUserId(d);
			this.showDealerSuggestions = false;
			this.errors.payment_by_id = "";
		},

		selectCustomer(c) {
			this.selectedCustomer = c;
			this.customerSearch = c.company_name || c.name || "";
			this.form.payment_by_id = this.getSelectedUserId(c);
			this.showCustomerSuggestions = false;
			this.errors.payment_by_id = "";
		},

		open(service) {
			this.getToken();
			this.service = service;
			this.errors = {};

			const rawPaymentBy = service?.payment_by ?? service?.payment_user_type;
			const paymentBy = rawPaymentBy != null && String(rawPaymentBy) !== ""
				? String(rawPaymentBy)
				: "1";
			const rawPaymentById = service?.payment_by_id ?? service?.payment_user_id;

			this.form = {
				amount: service?.total_amount != null ? String(service.total_amount) : "",
				payment_by: paymentBy === "2" ? "2" : "1",
				payment_by_id: rawPaymentById != null && rawPaymentById !== "" ? String(rawPaymentById) : "",
				cash_in_hand: service?.cash_in_hand != null ? String(service.cash_in_hand) : "0",
				cash_online: service?.cash_online != null ? String(service.cash_online) : "0",
				credit_amount: service?.credit_amount != null ? String(service.credit_amount) : "0",
				payment_id: service?.payment_id || "",
			};

			this.selectedDealer = null;
			this.selectedCustomer = null;
			this.dealerSearch = "";
			this.customerSearch = "";

			const paymentById = rawPaymentById;
			if (this.form.payment_by === "2") {
				if (paymentById != null && paymentById !== "") {
					const byUser = this.companies.find((c) => String(c.user_id) === String(paymentById));
					if (byUser) {
						this.selectCustomer(byUser);
					} else {
						this.form.payment_by_id = String(paymentById);
						this.customerSearch = "User #" + paymentById;
						this.selectedCustomer = {
							company_name: this.customerSearch,
							user_id: paymentById,
						};
					}
				}
			} else if (paymentById != null && paymentById !== "") {
				const byUser = this.dealers.find((d) => String(d.user_id) === String(paymentById));
				if (byUser) {
					this.selectDealer(byUser);
				} else if (service?.dealer_id != null) {
					const byId = this.dealers.find((d) => String(d.id) === String(service.dealer_id) || String(d.user_id) === String(service.dealer_id));
					if (byId) this.selectDealer(byId);
					else {
						this.dealerSearch = service?.dealer_name || ("User #" + paymentById);
						this.selectedDealer = {
							first_name: this.dealerSearch,
							user_id: paymentById,
						};
						this.form.payment_by_id = String(paymentById);
					}
				} else {
					this.form.payment_by_id = String(paymentById);
					this.dealerSearch = service?.dealer_name || ("User #" + paymentById);
					this.selectedDealer = {
						first_name: this.dealerSearch,
						user_id: paymentById,
					};
				}
			} else if (service?.dealer_id != null) {
				const byId = this.dealers.find((d) => String(d.id) === String(service.dealer_id) || String(d.user_id) === String(service.dealer_id));
				if (byId) this.selectDealer(byId);
				else {
					this.dealerSearch = service?.dealer_name || "";
					if (service?.dealer_user_id) {
						this.form.payment_by_id = String(service.dealer_user_id);
						this.selectedDealer = {
							id: service.dealer_id,
							first_name: service.dealer_name || "",
							user_id: service.dealer_user_id,
						};
					}
				}
			}

			if (this.modalInstance) this.modalInstance.show();
		},

		close() {
			if (this.modalInstance) this.modalInstance.hide();
			this.service = null;
			this.errors = {};
			this.submitting = false;
		},

		validate() {
			this.errors = {};
			const amount = parseFloat(this.form.amount);
			if (this.form.amount === "" || isNaN(amount) || amount < 0) {
				this.errors.amount = "Enter a valid total amount.";
			}
			if (!this.form.payment_by || !["1", "2"].includes(String(this.form.payment_by))) {
				this.errors.payment_by = "Please select payment user type.";
			}
			if (!this.form.payment_by_id) {
				this.errors.payment_by_id =
					this.form.payment_by === "2" ? "Please select a customer." : "Please select a dealer.";
			}

			if (this.hasExistingPayment) {
				const cash = parseFloat(this.form.cash_in_hand);
				const online = parseFloat(this.form.cash_online);
				const credit = parseFloat(this.form.credit_amount);
				if (this.form.cash_in_hand === "" || isNaN(cash) || cash < 0) {
					this.errors.cash_in_hand = "Enter a valid cash amount.";
				}
				if (this.form.cash_online === "" || isNaN(online) || online < 0) {
					this.errors.cash_online = "Enter a valid online amount.";
				}
				if (this.form.credit_amount === "" || isNaN(credit) || credit < 0) {
					this.errors.credit_amount = "Enter a valid credit amount.";
				}
				const paid = (isNaN(cash) ? 0 : cash) + (isNaN(online) ? 0 : online) + (isNaN(credit) ? 0 : credit);
				if (!isNaN(amount) && paid > amount + 0.01) {
					this.errors.form = "Paid amount cannot exceed total amount.";
				}
				if (!isNaN(online) && online > 0.01 && !(this.form.payment_id || "").trim()) {
					this.errors.payment_id = "Payment ID is required for online payment.";
				}
			}

			return Object.keys(this.errors).length === 0;
		},

		submit() {
			if (!this.service || !this.validate()) return;
			this.getToken();
			this.submitting = true;

			const payload = {
				service_id: this.service.id,
				amount: this.form.amount,
				payment_by: Number(this.form.payment_by),
				payment_by_id: this.form.payment_by_id,
			};

			if (this.hasExistingPayment) {
				payload.cash_in_hand = this.form.cash_in_hand;
				payload.cash_online = this.form.cash_online;
				payload.credit_amount = this.form.credit_amount;
				payload.payment_id = (this.form.payment_id || "").trim() || null;
			}

			this.$axios
				.post("/staffs/updateServiceData", payload, {
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const ok = res.data?.status === true || res.data?.status === "true";
					if (!ok) {
						const msg = res.data?.message;
						this.errors.form =
							typeof msg === "string"
								? msg
								: msg?.payment_id?.[0] || msg?.amount?.[0] || "Update failed";
						return;
					}
					this.$swal("", res.data?.message || "Service data updated", "success");
					this.close();
					this.$emit("updated");
				})
				.catch((err) => {
					const msg = err?.response?.data?.message;
					if (typeof msg === "string") {
						this.errors.form = msg;
					} else if (msg && typeof msg === "object") {
						const first = Object.values(msg).flat()?.[0];
						this.errors.form = first || "Update failed";
					} else {
						this.errors.form = "Update failed";
					}
				})
				.finally(() => {
					this.submitting = false;
				});
		},
	},
};
</script>
